// components/Carousels/IndustryCarousel.tsx
"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import Image from "next/image";
import Link from "next/link";

// Import Swiper styles
import "swiper/css";
import { Typography } from "../ui/Typography";

interface Industry {
  imageUrl: string;
  screenUrl: string;
  title: string;
  description: string;
  link: string;
}

interface IndustryCarouselProps {
  industries: Industry[];
  imgBaseUrl: string;
  textCenter?: boolean;
  noScreen?: boolean;
  forBlog?: boolean;
}

export default function IndustryCarousel({
  industries,
  imgBaseUrl,
  textCenter = true,
  noScreen = false,
  forBlog = false,
}: IndustryCarouselProps) {
  return (
    <div className="swiper-container industrySlider">
      <Swiper
        modules={[Autoplay]}
        spaceBetween={forBlog ? 30 : 10}
        slidesPerView={1}
        breakpoints={{
          640: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: forBlog ? 3 : 4 },
          1280: { slidesPerView: forBlog ? 3 : 4 },
        }}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
          waitForTransition: true,
        }}
        loop={true}
        navigation={{
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        }}
        pagination={{
          clickable: true,
          el: ".swiper-pagination",
        }}
        speed={500}
        className="industry-swiper"
      >
        {industries.map((industry, index) => (
          <SwiperSlide key={index}>
            <Link href={industry.link} className="card group block">
              <div className="image relative ">
                {/* Background Image */}
                <picture className="bg-img after:absolute after:inset-0 after:bg-gradient-to-t after:from-black after:to-transparent after:opacity-60 after:pointer-events-none group-hover:after:opacity-0">
                  <Image
                    src={imgBaseUrl + industry.imageUrl}
                    alt={industry.title}
                    title={industry.title}
                    width={500}
                    height={334}
                    loading="lazy"
                    decoding="async"
                    className="img-fluid w-full h-auto object-cover"
                    style={{ width: "100%", height: "auto" }}
                  />
                </picture>
                {/* Screen Image stacked on top with 80% width */}
                {!noScreen && (
                  <div className="absolute z-10 inset-0 flex items-center justify-center">
                    <div className="w-[80%] h-auto">
                      <picture className="screen">
                        <Image
                          src={imgBaseUrl + industry.screenUrl}
                          alt={industry.title}
                          title={industry.title}
                          width={329}
                          height={185}
                          loading="lazy"
                          decoding="async"
                          className="img-fluid w-full h-auto object-cover border-2 border-black"
                        />
                      </picture>
                    </div>
                  </div>
                )}
              </div>
              <div className={`${forBlog ? "p-0 py-4" : "p-4"} ${textCenter ? "text-center" : ""}`}>
                <Typography variant="h4" as="h3" className="mb-2">{industry.title}</Typography>
                <Typography variant="small" as="p">{industry.description}</Typography>

                {forBlog && <span className="text-secondary text-sm mt-4 block">Read More</span>}
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Navigation buttons */}
      <div className="swiper-button-prev !text-primary !w-12 !h-12 !mt-0 !-translate-y-1/2 after:!text-xl"></div>
      <div className="swiper-button-next !text-primary !w-12 !h-12 !mt-0 !-translate-y-1/2 after:!text-xl"></div>

      {/* Pagination */}
      <div className="swiper-pagination !relative !mt-8"></div>
    </div>
  );
}
